import type { IErrorState } from "../shared/error/interfaces/error";
import type { ICashDepositContext } from "../interfaces/cash-deposit";
import { isHttpUrl } from "./live-exit-url";

/** Funding statuses that can still mint / show a barcode. */
export const MINTABLE_FUNDING_STATUSES = [
  "awaiting_funding",
  "barcode_issued",
] as const;

/** Operation left the funding window — cash accepted or already settled. */
const FUNDING_SUCCEEDED_STATUSES = new Set(["processing", "completed"]);

/** Operation died while the user was still on the barcode funnel. */
const FUNDING_FAILED_STATUSES = new Set(["failed", "cancelled"]);

export function isCashDepositFundingSucceeded(operation: {
  status?: string;
  fundingStatus?: string;
}): boolean {
  return (
    operation.fundingStatus === "funded" ||
    FUNDING_SUCCEEDED_STATUSES.has(operation.status ?? "")
  );
}

export function isCashDepositFundingFailed(operation: {
  status?: string;
}): boolean {
  return FUNDING_FAILED_STATUSES.has(operation.status ?? "");
}

/**
 * True when the claimed cash-deposit link can no longer mint a barcode
 * because the pinned operation already left the funding window.
 */
export function isCashDepositAlreadyFunded(
  context: ICashDepositContext | null | undefined,
): boolean {
  const status = context?.fundingStatus;
  if (!status) return false;
  return !(MINTABLE_FUNDING_STATUSES as readonly string[]).includes(status);
}

export function buildAlreadyFundedErrorState(
  t: (key: string) => string,
  callbackUrl?: string,
): IErrorState {
  const canExit = isHttpUrl(callbackUrl);
  return {
    title: t("error.operationAlreadyFundedTitle"),
    subTitle: t("error.operationAlreadyFundedSubtitle"),
    message: t("error.operationAlreadyFundedMessage"),
    btnText: canExit ? t("error.operationAlreadyFundedCta") : null,
    isOut: canExit,
    pageUrl: canExit ? callbackUrl : undefined,
  };
}
